import React, { forwardRef, useState } from "react";
import {
  Alert as MuiAlert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileUpload from "react-mui-fileuploader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import uploadImg from "./../images/upload.svg";

const FIELD_LABELS = {
  email: "Email",
  firstName: "First Name",
  middleName: "Middle Name",
  lastName: "Last Name",
  phone: "Phone",
  addressLine1: "Address Line 1",
  addressLine2: "Address Line 2",
  city: "City",
  state: "State",
  zipCode: "Zip Code",
  mailingAddressLine1: "Mailing Address Line 1",
  mailingAddressLine2: "Mailing Address Line 2",
  mailingCity: "Mailing City",
  mailingState: "Mailing State",
  mailingZipCode: "Mailing Zip Code",
  birthDate: "Birth Date",
  socialSecurityNumber: "Social Security Number",
  idEmployee: "Employee ID",
  idEmployer: "Employer ID",
  idLanguage: "Language",
  cardType: "Card Type",
  cardIdentifier: "Card Identifier",
  cardProxy: "Card Proxy",
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function OnboardingUpload({ setOpen, employerId }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState("pick");
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [availableCards, setAvailableCards] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [useLocationAddressForEmpty, setUseLocationAddressForEmpty] =
    useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const showError = (err) =>
    setFeedback({
      open: true,
      type: "error",
      message: err?.response?.data ?? err.message,
    });

  const isUnmapped = (value) => value == null || value < 0;
  const needsCardSelection = (currentMapping) =>
    isUnmapped(currentMapping?.cardIdentifier) &&
    isUnmapped(currentMapping?.cardProxy) &&
    isUnmapped(currentMapping?.cardType);

  const fetchAvailableCards = async (locations = []) => {
    const locationQuery = locations.length
      ? `&locations=${encodeURIComponent(locations.join(","))}`
      : "";
    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/paycards/available?page=0&pageSize=1000&employerIdentifier=${encodeURIComponent(employerId ?? "")}${locationQuery}`
    );
    return response.data ?? [];
  };

  const uploadMutation = useMutation(
    async ({ file, confirmedMapping, locations = [] }) => {
      const base64Content = await toBase64(file);
      return HttpService.getAxiosClient().post(
        window.API_URL + "/onboarding/upload-mapped",
        {
          fileName: file.name,
          employerId,
          base64Content,
          mapping: JSON.stringify(confirmedMapping),
          selectedLocationIds: JSON.stringify(locations),
          useLocationAddressForEmpty,
        }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bulk_onboardings"] });
        if (setOpen) setOpen(false);
        setFeedback({
          open: true,
          type: "success",
          message: "File uploaded successfully",
        });
      },
      onError: showError,
    }
  );

  const analyzeMutation = useMutation(
    async (file) => {
      const base64Content = await toBase64(file);
      return HttpService.getAxiosClient().post(
        window.API_URL + "/onboarding/analyze",
        {
          fileName: file.name,
          employerId,
          base64Content,
        }
      );
    },
    {
      onSuccess: (response) => {
        const result = response.data;
        const initialMapping = {};
        (result.allFields ?? []).forEach((field) => {
          initialMapping[field] = result.proposedMapping?.[field] ?? -1;
        });
        setAnalysisResult(result);
        setMapping(initialMapping);
        if (needsCardSelection(result.proposedMapping)) {
          fetchAvailableCards()
            .then((cards) => {
              setAvailableCards(cards);
              setStep("bundles");
            })
            .catch(showError);
        } else if (result.confident) {
          uploadMutation.mutate({
            file: selectedFile,
            confirmedMapping: result.proposedMapping,
          });
        } else {
          setStep("confirm");
        }
      },
      onError: showError,
    }
  );

  const isLoading = analyzeMutation.isLoading || uploadMutation.isLoading;
  const required = new Set(analysisResult?.requiredFields ?? []);
  const mappedValues = () =>
    Object.fromEntries(
      Object.entries(mapping).filter(([, index]) => index >= 0)
    );

  const confirmUpload = () => {
    const missing = [...required].filter((field) => (mapping[field] ?? -1) < 0);
    if (missing.length) {
      setFeedback({
        open: true,
        type: "error",
        message: `Please assign columns for required fields: ${missing.map((field) => FIELD_LABELS[field] ?? field).join(", ")}`,
      });
      return;
    }
    if (needsCardSelection(mapping)) {
      fetchAvailableCards()
        .then((cards) => {
          setAvailableCards(cards);
          setStep("bundles");
        })
        .catch(showError);
      return;
    }
    uploadMutation.mutate({
      file: selectedFile,
      confirmedMapping: mappedValues(),
    });
  };

  if (step === "pick")
    return (
      <Box component="form" noValidate autoComplete="off">
        <Grid container spacing={1.5} sx={{ ml: 2, mr: 1 }}>
          <Container maxWidth="xlg">
            <Grid container spacing={1.5} sx={{ maxWidth: "80%" }}>
              <Grid item xs={12} md={4}>
                <br />
                <br />
                <img src={uploadImg} alt="upload" width="50%" loading="lazy" />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom>
                  Upload Onboarding File
                </Typography>
                <FileUpload
                  multiFile={false}
                  onFilesChange={(files) =>
                    setSelectedFile(files.length === 1 ? files[0] : null)
                  }
                  title="Please upload the CSV or XLSX file"
                  showPlaceholderImage={false}
                  allowedExtensions={[
                    "csv",
                    "xlsx",
                    "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  ]}
                  maxUploadFiles={1}
                  header="> DROP HERE <"
                  onContextReady={() => {}}
                />
                <Button
                  disabled={!selectedFile || isLoading}
                  sx={{ mt: 1 }}
                  onClick={() => analyzeMutation.mutate(selectedFile)}
                  variant="contained"
                  startIcon={
                    isLoading ? (
                      <CircularProgress size="1rem" color="inherit" />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                >
                  Analyze &amp; Upload
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Grid>
        <Feedback feedback={feedback} setFeedback={setFeedback} Alert={Alert} />
      </Box>
    );

  const locationOptions = Object.values(
    availableCards.reduce((locations, card) => {
      if (card.idLocation) {
        const location = locations[card.idLocation] ?? {
          id: card.idLocation,
          description: card.locationDescription || card.idLocation,
          availableCount: 0,
        };
        location.availableCount += 1;
        locations[card.idLocation] = location;
      }
      return locations;
    }, {})
  );

  if (step === "bundles")
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Locations for Assignment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cards will be selected automatically for each source row from the
          locations you choose.
        </Typography>
        <Autocomplete
          multiple
          options={locationOptions}
          value={locationOptions.filter((location) =>
            selectedLocations.includes(location.id)
          )}
          onChange={(event, values) => {
            setSelectedLocations(values.map((location) => location.id));
          }}
          getOptionLabel={(location) =>
            `${location.description} - ${location.availableCount} available cards [${location.id}]`
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Available locations" />
          )}
        />
        <FormControlLabel
          sx={{ mt: 2, display: "flex" }}
          control={
            <Checkbox
              checked={useLocationAddressForEmpty}
              onChange={(event) =>
                setUseLocationAddressForEmpty(event.target.checked)
              }
            />
          }
          label="Use the selected location address for empty member address fields"
        />
        {!availableCards.length && (
          <Typography color="error" sx={{ mt: 2 }}>
            No available cards were found for this employer.
          </Typography>
        )}
        <Box display="flex" gap={2} mt={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => setStep("confirm")}
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              uploadMutation.mutate({
                file: selectedFile,
                confirmedMapping: mappedValues(),
                locations: selectedLocations,
              })
            }
            disabled={isLoading || selectedLocations.length === 0}
          >
            {isLoading ? (
              <CircularProgress size="1.5rem" color="inherit" />
            ) : (
              "Assign & Upload"
            )}
          </Button>
        </Box>
        <Feedback feedback={feedback} setFeedback={setFeedback} Alert={Alert} />
      </Box>
    );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirm Column Mapping
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review how the uploaded columns map to onboarding fields.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Field</strong>
            </TableCell>
            <TableCell>
              <strong>Mapped to Column</strong>
            </TableCell>
            <TableCell>
              <strong>Sample Value</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(analysisResult?.allFields ?? []).map((field) => {
            const currentIndex = mapping[field] ?? -1;
            const isRequired = required.has(field);
            const hasError = isRequired && currentIndex < 0;
            return (
              <TableRow
                key={field}
                sx={hasError ? { backgroundColor: "#fff5f5" } : {}}
              >
                <TableCell>
                  {FIELD_LABELS[field] ?? field}
                  {isRequired && (
                    <Chip
                      label="required"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 240 }}
                    error={hasError}
                  >
                    <Select
                      value={currentIndex}
                      onChange={(event) =>
                        setMapping((previous) => ({
                          ...previous,
                          [field]: Number(event.target.value),
                        }))
                      }
                    >
                      <MenuItem value={-1}>
                        <em>-- not in file --</em>
                      </MenuItem>
                      {(analysisResult?.headers ?? []).map((header, index) => (
                        <MenuItem key={index} value={index}>
                          {header} (col {index})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {currentIndex >= 0
                      ? (analysisResult?.sampleRow?.[currentIndex] ?? "")
                      : ""}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box display="flex" gap={2} mt={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setStep("pick")}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={confirmUpload}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size="1.5rem" color="inherit" />
          ) : (
            "Confirm & Upload"
          )}
        </Button>
      </Box>
      <Feedback feedback={feedback} setFeedback={setFeedback} Alert={Alert} />
    </Box>
  );
}

function Feedback({ feedback, setFeedback, Alert }) {
  return (
    <Snackbar
      open={feedback.open}
      autoHideDuration={6000}
      onClose={() => setFeedback({ ...feedback, open: false })}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setFeedback({ ...feedback, open: false })}
        severity={feedback.type}
        sx={{ width: "100%" }}
      >
        {feedback.message}
      </Alert>
    </Snackbar>
  );
}
